import { useWeeklySummaries } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate, formatNumber } from '../../lib/format';
import { highlightMessageKey, isBlankWeek, weightDelta } from '../../lib/summary';
import type { WeeklySummaryRow } from '../../api/types';
import { Badge, Card, CardHeader, ProgressBar, SkeletonCard, StatTile } from '../../ui';

/**
 * Tjedni sažetak: zadnji tjedan kao istaknuta kartica, stariji kao popis.
 *
 * Tjedan bez ijednog traga server ne generira (`apps/api/src/scheduled.ts`), pa
 * ekran nikad ne pokazuje praznu karticu — ako takav redak ipak stigne,
 * `isBlankWeek` ga izbacuje prije prikaza.
 */
export function WeeklySummarySection() {
  const { t } = useTranslation();
  const query = useWeeklySummaries();

  return (
    <section data-testid="weekly-summary">
      <h2 className="eyebrow mb-3">{t('summary.title')}</h2>
      <QueryBoundary
        query={query}
        skeleton={<SkeletonCard />}
        isEmpty={(d) => d.items.filter((w) => !isBlankWeek(w)).length === 0}
        empty={{ message: t('summary.empty') }}
      >
        {(data) => {
          const weeks = data.items.filter((w) => !isBlankWeek(w));
          const [latest, ...older] = weeks;
          if (!latest) return null;
          return (
            <div className="flex flex-col gap-4">
              <LatestWeek week={latest} />
              {older.length > 0 && (
                <div>
                  <h3 className="eyebrow mb-2">{t('summary.earlier')}</h3>
                  <ul className="flex flex-col gap-2">
                    {older.map((week) => (
                      <li key={week.weekStart}>
                        <OlderWeek week={week} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        }}
      </QueryBoundary>
    </section>
  );
}

function LatestWeek({ week }: { week: WeeklySummaryRow }) {
  const { t, locale } = useTranslation();
  const delta = weightDelta(week);
  const highlights = week.highlights
    .map((key) => highlightMessageKey(key))
    .filter((key): key is string => key !== null);

  return (
    <Card raised data-testid="summary-latest">
      <CardHeader
        eyebrow={t('summary.lastWeek')}
        title={t('summary.weekOf', { date: formatDate(`${week.weekStart}T00:00:00.000Z`, locale) })}
        action={<Badge tone="accent">{t('summary.consistencyValue', { pct: week.consistencyPct })}</Badge>}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={t('summary.workouts')}
          value={`${week.workoutsCompleted}/${week.workoutsPrescribed}`}
        />
        <StatTile
          label={t('summary.steps')}
          value={week.steps === null ? '—' : formatNumber(week.steps, locale)}
        />
        <StatTile
          label={t('summary.averageMinutes')}
          value={week.averageMinutes === null ? '—' : week.averageMinutes}
          {...(week.averageMinutes === null ? {} : { unit: 'min' })}
        />
        <StatTile
          label={t('dash.weight')}
          value={
            week.weightStart === null || week.weightEnd === null
              ? '—'
              : `${formatNumber(week.weightStart, locale, 1)} → ${formatNumber(week.weightEnd, locale, 1)}`
          }
          {...(week.weightStart === null || week.weightEnd === null ? {} : { unit: 'kg' })}
          {...(delta === null || delta === 0
            ? {}
            : {
                delta: {
                  value: `${delta > 0 ? '+' : ''}${formatNumber(delta, locale, 1)} kg`,
                  // Manje kilograma je zeleno samo zato što je to smjer koji je klijent tražio.
                  direction: delta < 0 ? ('down' as const) : ('up' as const),
                },
              })}
        />
      </div>

      <ProgressBar
        className="mt-5"
        value={week.consistencyPct}
        max={100}
        label={t('summary.consistency')}
        valueLabel={t('summary.consistencyValue', { pct: week.consistencyPct })}
      />

      {highlights.length > 0 && (
        <ul className="mt-5 flex flex-col gap-2" data-testid="summary-highlights">
          {highlights.map((key) => (
            <li
              key={key}
              className="rounded-md bg-bg-alt px-4 py-3 text-[14px] leading-relaxed text-ink"
            >
              {t(key)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function OlderWeek({ week }: { week: WeeklySummaryRow }) {
  const { t, locale } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line-subtle bg-surface px-4 py-3 shadow-card">
      <span className="text-[14px] text-ink">
        {t('summary.weekOf', { date: formatDate(`${week.weekStart}T00:00:00.000Z`, locale) })}
      </span>
      <span className="flex items-center gap-3 text-[13px] text-ink-muted">
        <span>
          {t('summary.workouts')}: {week.workoutsCompleted}/{week.workoutsPrescribed}
        </span>
        <Badge>{t('summary.consistencyValue', { pct: week.consistencyPct })}</Badge>
      </span>
    </div>
  );
}
